import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { raiseErrorBanner } from '../../../../MessageDispatch/messageDispatch';
import { catchAxiosError } from '../../../../../services/axios/_utility';
import { getExistingEmail } from './_api';
import useFormInput from '../../../../../utils/hooks/useFormInput';
import { debounceAsync } from '../../../../../utils/misc/debounce';
import TextBox from '../../TextBox';
import { LABEL_POSITIONS } from '../../../_constants';

/**
 * Formik-compatible functional component for consuming, validating UPMC email address inputs. Performs regular expression, delayed server-side existence validation.
 *
 * @param {Object} props JSX attributes passed to component.
 * @returns {ReactElement} rendered component.
 */
const EmailTextBox = ({ labelPosition, formik, ...props }) => {
	const VALIDATION_MESSAGE = 'Please provide a valid UPMC email address';

	const dispatch = useDispatch();
	const { meta: { error, touched } = {} } = useFormInput(props.name);
	const [sCustomError, setCustomError] = useState('');

	/**
	 * Lifecycle hook on formik prop change. Updates control validation message when field
	 * is touched and noted by formik as invalid.
	 *
	 * @function
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		setCustomError(touched && error ? error : '');
	}, [touched, error]);

	/**
	 * Determines if @param email is valid UPMC email based on existence.
	 *
	 * @function
	 * @param {string} email non-null email to validate.
	 * @returns {Promise} resolved promise providing error message if email invalid, otherwise undefined.
	 */
	const validate = async email => {
		try {
			let errorMessage;
			const emailExisting = await getExistingEmail(String(email).trim());
			errorMessage =
				emailExisting !== null && emailExisting.length > 0
					? errorMessage
					: VALIDATION_MESSAGE;
			setCustomError(errorMessage ? errorMessage : '');
			return errorMessage;
		} catch (error) {
			raiseErrorBanner(catchAxiosError(error), dispatch);
		}
	};

	/**
	 * Memoized callback debouncing `validate`.
	 *
	 * @type {Function}
	 * @returns {Promise<string>} nullable validation message on error;
	 */
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedValidate = useCallback(
		debounceAsync(value => validate(value)),
		[]
	);

	return (
		<TextBox
			help='Must be a valid UPMC email.'
			inputProps={{
				required: props.required,
				customError: sCustomError,
				type: 'email',
				...props.inputProps,
			}}
			label='Email Address'
			labelPosition={labelPosition ? labelPosition : LABEL_POSITIONS.TOP}
			name={props.name}
			validate={value => debouncedValidate(value)}
			{...props}
		/>
	);
};

EmailTextBox.propTypes = {};

export { EmailTextBox };
